import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  type AnalyticalVariableListItem,
  useAnalyticalVariableListQuery,
} from '@verantos-dev/feature-analytical-variables';

import { selectVariables } from '../state/selectors';
import { useAnalyticalDatasetDefinitionStore } from '../state/useAnalyticalDatasetDefinitionStore';
import type {
  AnalyticalVariableDisplay,
  AnalyticalVariableFormat,
  UseAnalyticalVariablesSectionResult,
} from '../types';
import { useAdRouteParams } from './useAdRouteParams';
import { useAnalyticalDatasetEditLock } from './useAnalyticalDatasetEditLock';

const FORMAT_FALLBACK: AnalyticalVariableFormat = 'String';
const VALID_FORMATS: ReadonlySet<AnalyticalVariableFormat> = new Set([
  'String',
  'Date',
  'Numeric',
  'Boolean',
]);

// API returns uppercase (`STRING`); card expects titlecase. Unknown values
// fall back to `String` so a new server-side format doesn't crash the row.
const normalizeFormat = (raw: string | null | undefined): AnalyticalVariableFormat => {
  if (!raw) return FORMAT_FALLBACK;
  const titled = (raw.charAt(0).toUpperCase() +
    raw.slice(1).toLowerCase()) as AnalyticalVariableFormat;
  return VALID_FORMATS.has(titled) ? titled : FORMAT_FALLBACK;
};

const padSerial = (n: number): string => String(n).padStart(2, '0');

// Synthetic UI-only ids — the store never holds these so picker/store logic
// can't accidentally remove or duplicate the default rows.
interface DefaultVariableSpec {
  id: string;
  nameKey: string;
  descriptionKey: string;
  format: AnalyticalVariableFormat;
}

const DEFAULT_VARIABLES: ReadonlyArray<DefaultVariableSpec> = [
  {
    id: '__default_patient_id__',
    nameKey: 'analyticalDatasetDetail.variables.defaultPatientId.name',
    descriptionKey: 'analyticalDatasetDetail.variables.defaultPatientId.description',
    format: 'String',
  },
  {
    id: '__default_index_date__',
    nameKey: 'analyticalDatasetDetail.variables.defaultIndexDate.name',
    descriptionKey: 'analyticalDatasetDetail.variables.defaultIndexDate.description',
    format: 'Date',
  },
];

export const useAnalyticalVariablesSection = (): UseAnalyticalVariablesSectionResult => {
  const { formatMessage } = useIntl();
  const { projectId } = useAdRouteParams();
  const variables = useAnalyticalDatasetDefinitionStore(selectVariables);
  const removeVariable = useAnalyticalDatasetDefinitionStore((s) => s.removeVariable);
  const { isEditDisabled, isProcessing } = useAnalyticalDatasetEditLock();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [previewAvId, setPreviewAvId] = useState<string | null>(null);

  const {
    data: listResponse,
    isLoading: isAvListLoading,
    isFetching: isAvListFetching,
  } = useAnalyticalVariableListQuery(undefined, projectId);

  const itemsById = useMemo(() => {
    const map = new Map<string, AnalyticalVariableListItem>();
    for (const item of listResponse?.data ?? []) {
      map.set(item.analyticalVariableId, item);
    }
    return map;
  }, [listResponse]);

  const rows = useMemo<AnalyticalVariableDisplay[]>(() => {
    const defaultRows = DEFAULT_VARIABLES.map<AnalyticalVariableDisplay>((spec, idx) => ({
      analyticalVariableId: spec.id,
      serialNumber: padSerial(idx + 1),
      name: formatMessage({ id: spec.nameKey }),
      description: formatMessage({ id: spec.descriptionKey }),
      format: spec.format,
      isDefault: true,
    }));

    const userRows = variables.flatMap<AnalyticalVariableDisplay>((entry, idx) => {
      const item = itemsById.get(entry.avId);
      if (!item) return [];
      return [
        {
          analyticalVariableId: entry.avId,
          serialNumber: padSerial(idx + DEFAULT_VARIABLES.length + 1),
          name: item.dataElement ?? item.name ?? '',
          description: item.description ?? '',
          format: normalizeFormat(item.format),
          isDefault: false,
        },
      ];
    });

    return [...defaultRows, ...userRows];
  }, [variables, itemsById, formatMessage]);

  const onAddVariable = useCallback(() => setIsPickerOpen(true), []);
  const onClosePicker = useCallback(() => setIsPickerOpen(false), []);
  const onRemoveVariable = useCallback((avId: string) => removeVariable(avId), [removeVariable]);
  const onViewVariable = useCallback((avId: string) => setPreviewAvId(avId), []);
  const onClosePreview = useCallback(() => setPreviewAvId(null), []);

  // True while user-added variables exist in the store but the AV library
  // hasn't returned yet — the join in `userRows` silently filters them out
  // until the lookup resolves, which would otherwise look like data loss.
  const isLoading = variables.length > 0 && (isAvListLoading || isAvListFetching);

  const editLockTooltip = isEditDisabled
    ? formatMessage({
        id: isProcessing
          ? 'analyticalDatasetDetail.editLock.processing'
          : 'analyticalDatasetDetail.editLock.notOwner',
      })
    : undefined;

  return {
    sectionLabel: formatMessage({ id: 'analyticalDatasetDetail.variables.label' }),
    count: rows.length,
    addLabel: formatMessage({ id: 'analyticalDatasetDetail.variables.addButton' }),
    emptyLabel: formatMessage({ id: 'analyticalDatasetDetail.variables.empty' }),
    rows,
    isPickerOpen,
    isLoading,
    onAddVariable,
    onClosePicker,
    onRemoveVariable,
    previewAvId,
    onViewVariable,
    onClosePreview,
    isEditDisabled,
    editLockTooltip,
  };
};
